import React, { useState, useEffect } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { Menu, X, ArrowUpRight } from "lucide-react";

const navLinks = [
  { label: "Home", to: "/" },
  { label: "About", to: "/about" },
  { label: "Products", to: "/products" },
  { label: "Testimonials", to: "/#testimonials" },
];

export default function Header() {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const { pathname, hash } = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    const handler = () => setScrolled(window.scrollY > 8);
    handler();
    window.addEventListener("scroll", handler);
    return () => window.removeEventListener("scroll", handler);
  }, []);

  useEffect(() => {
    setOpen(false);
  }, [pathname, hash]);

  const handleContactClick = (e) => {
    e.preventDefault();
    if (pathname === "/") {
      const el = document.getElementById("contact");
      if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
    } else {
      navigate("/#contact");
    }
  };

  const isActive = (to) => {
    if (to === "/") return pathname === "/" && !hash;
    if (to.startsWith("/#")) return pathname === "/" && hash === to.slice(1);
    return pathname === to;
  };

  return (
    <header
      data-testid="site-header"
      className={`fixed top-0 inset-x-0 z-50 glass-header transition-all duration-300 ${scrolled ? "py-2" : "py-3"}`}
    >
      <div className="max-w-7xl mx-auto px-6 md:px-12 flex items-center justify-between">
        <Link to="/" data-testid="logo-link" className="flex items-center group" aria-label="Marpay Biotec">
          <img
            src="logo_icon.png"
            alt="Marpay Biotec"
            className="h-12 w-auto object-contain rounded-md"
            loading="eager"
          />
        </Link>

        <nav className="hidden lg:flex items-center gap-9">
          {navLinks.map((l) => (
            <Link
              key={l.to}
              to={l.to}
              data-testid={`nav-${l.label.toLowerCase().replace(/\s+/g, "-")}`}
              className={`text-sm font-medium transition-colors relative ${isActive(l.to) ? "text-[var(--mp-brand)]" : "text-[var(--mp-text-2)] hover:text-[var(--mp-text)]"
                }`}
            >
              {l.label}
              {isActive(l.to) && (
                <span className="absolute -bottom-1.5 left-0 right-0 h-0.5 rounded-full" style={{ background: "var(--mp-gold)" }} />
              )}
            </Link>
          ))}
        </nav>

        <div className="hidden lg:flex items-center gap-3">
          <a
            href="/#contact"
            onClick={handleContactClick}
            data-testid="header-cta-contact"
            className="btn-primary text-sm"
          >
            Partner with us <ArrowUpRight className="h-4 w-4" />
          </a>
        </div>

        <button
          data-testid="mobile-menu-toggle"
          className="lg:hidden inline-flex items-center justify-center h-10 w-10 rounded-full border border-[var(--mp-border)]"
          onClick={() => setOpen((s) => !s)}
          aria-label="Toggle menu"
        >
          {open ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
        </button>
      </div>

      {open && (
        <div className="lg:hidden border-t border-[var(--mp-border)] bg-white" data-testid="mobile-menu">
          <div className="px-6 py-6 flex flex-col gap-4">
            {navLinks.map((l) => (
              <Link
                key={l.to}
                to={l.to}
                onClick={() => setOpen(false)}
                data-testid={`mobile-nav-${l.label.toLowerCase().replace(/\s+/g, "-")}`}
                className="text-base font-medium text-[var(--mp-text)]"
              >
                {l.label}
              </Link>
            ))}
            <a
              href="/#contact"
              onClick={(e) => { setOpen(false); handleContactClick(e); }}
              data-testid="mobile-cta-contact"
              className="btn-primary text-sm w-fit"
            >
              Partner with us <ArrowUpRight className="h-4 w-4" />
            </a>
          </div>
        </div>
      )}
    </header>
  );
}
